import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DetailedOrderInQueue, OrderInQueue } from 'app/models/orderInQueue.model';
import { IAbstractItemInfo } from 'app/models/orderInfo.model';
import { Select } from '@ngxs/store';
import { OrdersState } from 'app/state-management/states/orders.states';

@UntilDestroy()
@Component({
  selector: 'app-order-list',
  templateUrl: './order-list.component.html',
  styleUrls: ['./order-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None
})
export class OrderListComponent implements OnInit {
  isLoading = true;

  orders: OrderInQueue[];
  // фронтовый фильтр по содержимому заказов: выбранные названия (логика ИЛИ)
  contentFilterSelection: string[] = [];
  contentNameSearch = '';
  allContentNames: string[] = [];
  filteredContentNames: string[] = [];
  filteredOrders$: Observable<OrderInQueue[]>;
  private selectedNames$ = new BehaviorSubject<string[]>([]);

  @Select(state => state.orders.ordersList) orders$: Observable<OrderInQueue[]>;
  @Select(state => state.orders.ordersListCount) ordersCount$: Observable<number>;
  @Select(OrdersState.currentOrder) currentOrder$: Observable<DetailedOrderInQueue>;
  @ViewChild('contentNameSearchInput') contentNameSearchInput: ElementRef<HTMLInputElement>;

  constructor(
  ) {
  }

  ngOnInit(): void {
    this.filteredOrders$ = combineLatest([this.orders$, this.selectedNames$])
      .pipe(
        map(([orders, selectedNames]) => this.filterOrdersByContent(orders ?? [], selectedNames))
      );

    this.orders$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(orders => {
        this.allContentNames = this.aggregateContentNames(orders ?? []);
        this.filteredContentNames = this.getFilteredContentNames();
        // список заказов перезагрузился — убираем из выбора названия,
        // которых в новом списке больше нет
        const stillPresent = this.contentFilterSelection.filter(name => this.allContentNames.includes(name));
        if (stillPresent.length !== this.contentFilterSelection.length) {
          this.onContentFilterChange(stillPresent);
        }
      });

    this.currentOrder$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(currentOrder => {
        if (currentOrder && currentOrder.details) {
          this.isLoading = false;
        } else {
          this.isLoading = true;
        }
      });
  }

  onContentFilterChange(selected: string[]): void {
    this.contentFilterSelection = selected;
    this.selectedNames$.next(selected);
  }

  clearContentFilter(event: Event): void {
    // не даём клику по крестику открыть выпадающий список
    event.stopPropagation();
    this.onContentFilterChange([]);
  }

  onContentNameSearch(value: string): void {
    this.contentNameSearch = value;
    this.filteredContentNames = this.getFilteredContentNames();
  }

  onContentPanelToggled(opened: boolean): void {
    if (opened) {
      this.contentNameSearchInput?.nativeElement.focus();
    } else {
      this.onContentNameSearch('');
    }
  }

  // пропускаем к mat-select только клавиши навигации, остальные глушим,
  // чтобы набор текста не срабатывал как typeahead по опциям
  onSearchKeydown(event: KeyboardEvent): void {
    const navigationKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'];
    if (!navigationKeys.includes(event.key)) {
      event.stopPropagation();
    }
  }

  private getFilteredContentNames(): string[] {
    const query = this.contentNameSearch.trim().toLowerCase();
    const matches = query
      ? this.allContentNames.filter(name => name.toLowerCase().includes(query))
      : this.allContentNames;
    // выбранные названия всегда держим в списке опций, иначе mat-select
    // потеряет их из значения при следующем изменении выбора
    const selectedMissing = this.contentFilterSelection.filter(name => !matches.includes(name));
    return [...selectedMissing, ...matches];
  }

  // уникальные названия блюд, модификаторов и продуктов комбо по текущему списку заказов
  private aggregateContentNames(orders: OrderInQueue[]): string[] {
    const unique = new Map<string, string>();
    for (const order of orders) {
      for (const name of this.collectContentNames(order)) {
        const key = name.toLowerCase();
        if (!unique.has(key)) {
          unique.set(key, name);
        }
      }
    }
    return [...unique.values()].sort((a, b) => a.localeCompare(b));
  }

  // заказ подходит, если содержит хотя бы одно из выбранных названий
  private filterOrdersByContent(orders: OrderInQueue[], selectedNames: string[]): OrderInQueue[] {
    if (selectedNames.length === 0) {
      return orders;
    }
    const terms = selectedNames.map(name => name.toLowerCase());
    return orders.filter(order => {
      const names = new Set(this.collectContentNames(order).map(name => name.toLowerCase()));
      return terms.some(term => names.has(term));
    });
  }

  // названия блюд, их модификаторов и продуктов внутри комбо (рекурсивно)
  private collectContentNames(order: OrderInQueue): string[] {
    const names: string[] = [];
    const collect = (item: IAbstractItemInfo): void => {
      if (item.name) {
        names.push(item.name);
      }
      for (const modifierGroup of item.modifierGroups ?? []) {
        for (const modifier of modifierGroup.modifiers ?? []) {
          if (modifier.name) {
            names.push(modifier.name);
          }
        }
      }
      for (const comboGroup of item.groups ?? []) {
        for (const comboItem of comboGroup.items ?? []) {
          collect(comboItem);
        }
      }
    };
    for (const item of order.metadata?.orderInfoDto?.items ?? []) {
      collect(item);
    }
    return names;
  }
}
